import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Select } from "../../../../components/forms/select";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Chip } from "../../../../components/common/chip";
import { ConfirmDialog } from "../../../../components/common/confirm-dialog";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useDetailApi } from "../../../../swr/useApi";
import { adminUsersService } from "../../../api_client";
import { useUpdateAdminUser, useDeactivateAdminUser } from "../../hooks/useAdminUserMutations";
import { toReadableDate } from "../../../../utils/date";
import type { UserDto } from "../../../../api";

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: user, isLoading, error } = useDetailApi<UserDto>({
    key: id ? `admin-user-detail-${id}` : null,
    serviceMethod: () => adminUsersService.adminGetUser(id!),
  });

  const { submit: updateUser, isLoading: isUpdating } = useUpdateAdminUser(id ?? "");
  const { submit: deactivateUser, isLoading: isDeactivating } = useDeactivateAdminUser(id ?? "");
  const { submit: reactivateUser, isLoading: isReactivating } = useUpdateAdminUser(id ?? "");

  const [pendingRole, setPendingRole] = useState<string>("");
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  useEffect(() => {
    if (user?.role) setPendingRole(user.role);
  }, [user?.role]);

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-8 py-8">
        <ShimmerLoader message="Loading user details…" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-8 py-8">
        <Text variant="body" tone="muted">User not found.</Text>
      </div>
    );
  }

  const hasChanges = pendingRole !== user.role;

  const handleSave = async () => {
    if (!hasChanges) return;
    const ok = await updateUser({ role: pendingRole as "admin" | "user" });
    if (ok) navigate(AppRoutes.ADMIN_USERS);
  };

  const handleDeactivate = async () => {
    const ok = await deactivateUser();
    if (ok) navigate(AppRoutes.ADMIN_USERS);
  };

  const handleReactivate = async () => {
    const ok = await reactivateUser({ isActive: true });
    if (ok) navigate(AppRoutes.ADMIN_USERS);
  };

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8">
      <Breadcrumb userName={user.name} onBack={() => navigate(AppRoutes.ADMIN_USERS)} />

      <div className="flex items-center gap-3">
        <Heading variant="h2" tone="dark">{user.name}</Heading>
        {user.isActive === false
          ? <Chip variant="rejected">Inactive</Chip>
          : <Chip variant="approved">Active</Chip>}
        {user.role === "admin"
          ? <Chip variant="underReview">Admin</Chip>
          : <Chip variant="tertiary">User</Chip>}
      </div>

      <UserInfoSection user={user} />

      <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6">
        <Heading variant="h3" tone="dark" className="mb-4">Actions</Heading>
        <div className="flex items-end gap-4">
          <div className="w-48">
            <Select
              name="user-role"
              label="Role"
              value={pendingRole}
              onChange={(e) => setPendingRole(e.target.value)}
              options={ROLE_OPTIONS}
            />
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            disabled={!hasChanges || isUpdating}
          >
            {isUpdating ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>

      {user.isActive === false ? (
        <div className="rounded-2xl border border-accentGreen/20 bg-bgWhite p-6">
          <Heading variant="h3" tone="dark" className="mb-2">Reactivate Account</Heading>
          <Text variant="bodySm" tone="muted" className="mb-4">
            This user is currently deactivated. Reactivating will restore their access.
          </Text>
          <Button
            variant="primary"
            size="md"
            onClick={handleReactivate}
            disabled={isReactivating}
          >
            {isReactivating ? "Reactivating…" : "Reactivate User"}
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-accentError/20 bg-bgWhite p-6">
          <Heading variant="h3" tone="dark" className="mb-2">Danger Zone</Heading>
          <Text variant="bodySm" tone="muted" className="mb-4">
            Deactivating this user will invalidate all their sessions and prevent login.
          </Text>
          <Button variant="secondaryDanger" size="md" onClick={() => setShowDeactivateDialog(true)}>
            Deactivate User
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
        onConfirm={handleDeactivate}
        title="Deactivate user"
        description={`This will deactivate ${user.name}'s account and invalidate all sessions. They will not be able to log in until reactivated.`}
        confirmText="Deactivate"
        loading={isDeactivating}
      />
    </div>
  );
}

function Breadcrumb({ userName, onBack }: { userName: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghostAccent"
        size="sm"
        leftIcon={<IconArrowLeft size={18} />}
        onClick={onBack}
        className="!h-auto !px-0 !py-0"
      >
        <Heading variant="h3" tone="inherit">User Management</Heading>
      </Button>
      <Text variant="bodySm" tone="muted">/</Text>
      <Heading variant="h3" tone="dark">{userName}</Heading>
    </div>
  );
}

function UserInfoSection({ user }: { user: UserDto }) {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-4 rounded-2xl border border-borderDefault bg-bgWhite p-6">
      <InfoRow label="Email" value={user.email} />
      <InfoRow label="Phone" value={user.phoneNumber} />
      <InfoRow label="Company" value={user.companyName} />
      <InfoRow label="CR Number" value={user.crNumber} />
      <InfoRow label="Tax Number" value={user.taxNumber} />
      <InfoRow label="Contact Name" value={user.contactName} />
      <InfoRow label="Partner ID" value={user.partnerId != null ? String(user.partnerId) : null} />
      <InfoRow label="Created" value={user.createdAt ? toReadableDate(user.createdAt) : null} />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text variant="bodySm" tone="muted">{label}</Text>
      <Text variant="body" tone="dark">{value || "—"}</Text>
    </div>
  );
}
